import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  memo,
} from "react";
import WaveSurfer from "wavesurfer.js";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  PlayActiveLightIcon,
  PauseActiveLightIcon,
  PlayIconActiveDark,
} from "../../../../assets/icon";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import { audioPlayerStyles } from "./styles";
import { useAppStore } from "../../../../stores/appStore";
import { sendAudioListeningTime } from "../../../../axios/user";
import useUserStore from "../../../../stores/userStore";
import { useMessageInteractionTracking } from "../../../../hooks/useMessageInteractionTracking";
import useChatStore from "../../../../stores/chatStore";
import MinimalAudioPlayer from "../minimal-audio-player/minimal-audio-player";
import { useBrowserDetection } from "../../../../hooks/useBrowserDetection";
import {
  fetchAuthenticatedFile,
  isProtectedApiFileUrl,
} from "../../../../utils/authenticatedFileUrl";
import { useAuthenticatedFileUrl } from "../../../../hooks/useAuthenticatedFileUrl";
import { AudioTranscriptionDialog } from "../audio-transcription-markdown/audio-transcription-dialog";
import type { TranscriptionTimedPayload } from "../../../../types";
import {
  createSyntheticPeaks,
  isCrossOriginAudioUrl,
  loadWaveSurferAudio,
} from "../../../../utils/waveSurferAudioLoad";

const durationCache = new Map<string, number>();
const TRANSCRIPT_SEEK_PREROLL_SEC = 0.18;

function cacheDuration(url: string, durationSec: number): number {
  durationCache.set(url, durationSec);
  try {
    localStorage.setItem(`audio_duration_${url}`, durationSec.toString());
  } catch {
    // ignore quota / private mode
  }
  return durationSec;
}

async function getDurationFromMediaElement(
  url: string,
  abortMs = 8000,
): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.preload = "metadata";

    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error("Timed out reading audio duration"));
    }, abortMs);

    const cleanup = () => {
      window.clearTimeout(timer);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("error", onError);
      audio.src = "";
    };

    const onMeta = () => {
      const d = audio.duration;
      cleanup();
      if (Number.isFinite(d) && d > 0) {
        resolve(d);
      } else {
        reject(new Error("Invalid audio duration"));
      }
    };

    const onError = () => {
      cleanup();
      reject(new Error("Failed to load audio metadata"));
    };

    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("error", onError);
    audio.src = url;
  });
}

async function getAccurateDuration(
  url: string,
  abortMs = 8000,
): Promise<number> {
  const savedDurationKey = `audio_duration_${url}`;
  const savedDuration = localStorage.getItem(savedDurationKey);
  if (savedDuration) {
    const parsed = parseFloat(savedDuration);
    if (!isNaN(parsed) && parsed > 0) {
      durationCache.set(url, parsed);
      return parsed;
    }
  }

  if (durationCache.has(url)) return durationCache.get(url)!;

  // Cross-origin hosts often allow media playback but block fetch (CORS).
  if (isCrossOriginAudioUrl(url)) {
    const d = await getDurationFromMediaElement(url, abortMs);
    return cacheDuration(url, d);
  }

  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), abortMs);

  try {
    const res = isProtectedApiFileUrl(url)
      ? await fetchAuthenticatedFile(url, {
          signal: ctrl.signal,
          cache: "force-cache",
        })
      : await fetch(url, { signal: ctrl.signal, cache: "force-cache" });
    clearTimeout(t);
    const buf = await res.arrayBuffer();

    const AC = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AC();
    const audioBuf: AudioBuffer = await ctx.decodeAudioData(buf);
    const d = audioBuf.duration;
    ctx.close();

    return cacheDuration(url, d);
  } catch (e) {
    clearTimeout(t);
    throw e;
  }
}

interface WaveformProps {
  audioUrl: string;
  theme: any;
  playbackRate: number;
  isUserRecorded?: boolean;
  onWaveformClick?: () => void;
  messageId?: string;
  height?: number;
}

const Waveform = memo(
  forwardRef<WaveSurfer | null, WaveformProps>(
    (
      {
        audioUrl,
        theme,
        playbackRate,
        isUserRecorded = false,
        onWaveformClick,
        messageId,
        height = 64,
      },
      ref,
    ) => {
      const containerRef = useRef<HTMLDivElement>(null);
      const waveSurferRef = useRef<WaveSurfer | null>(null);
      const prevTheme = useRef(theme.palette.mode);

      const shouldDisableSeeking = isUserRecorded;

      const createWS = useCallback(() => {
        const ws = WaveSurfer.create({
          container: containerRef.current!,
          waveColor: theme.palette.audioPlayer.waveColorEnd,
          progressColor: theme.palette.audioPlayer.waveColorStart,
          cursorWidth: 2,
          cursorColor: "#E6E6E680",
          height,
          barWidth: 3,
          barGap: 3,
          barRadius: 2,
          backend: "MediaElement",
          fillParent: true,
          dragToSeek: !shouldDisableSeeking,
          interact: !shouldDisableSeeking,
        });

        const mediaEl = ws.getMediaElement?.() as HTMLAudioElement | undefined;
        if (mediaEl && messageId) {
          mediaEl.preload = "metadata";
          mediaEl.setAttribute("data-message-id", messageId);
        }

        return ws;
      }, [
        theme.palette.audioPlayer.waveColorEnd,
        theme.palette.audioPlayer.waveColorStart,
        shouldDisableSeeking,
        messageId,
        height,
      ]);

      useEffect(() => {
        if (!containerRef.current) {
          return;
        }

        waveSurferRef.current = createWS();

        loadWaveSurferAudio(waveSurferRef.current, audioUrl).catch(
          (err: unknown) => {
            if ((err as DOMException).name !== "AbortError") {
              console.error(`WaveSurfer load error for ${audioUrl}:`, err);
            }
          },
        );

        if (ref) {
          typeof ref === "function"
            ? ref(waveSurferRef.current)
            : (ref.current = waveSurferRef.current);
        }
        return () => {
          waveSurferRef.current?.destroy();
        };
      }, [audioUrl, ref, theme, createWS, height]);

      useEffect(() => {
        if (!waveSurferRef.current) {
          return;
        }

        if (prevTheme.current === theme.palette.mode) {
          waveSurferRef.current.setPlaybackRate(playbackRate);
          return;
        }

        const wsOld = waveSurferRef.current;
        const wasPlaying = wsOld.isPlaying();
        const curTime = wsOld.getCurrentTime();
        const dur = wsOld.getDuration();

        try {
          wsOld.destroy();
        } catch (err: unknown) {
          if ((err as DOMException).name !== "AbortError") {
            console.error("Error destroying WaveSurfer:", err);
          }
        }

        waveSurferRef.current = createWS();
        loadWaveSurferAudio(waveSurferRef.current, audioUrl).catch(
          (err: unknown) => {
            if ((err as DOMException).name !== "AbortError") {
              console.error(`WaveSurfer reload error for ${audioUrl}:`, err);
            }
          },
        );

        if (ref) {
          typeof ref === "function"
            ? ref(waveSurferRef.current)
            : (ref.current = waveSurferRef.current);
        }

        waveSurferRef.current.on("ready", () => {
          if (dur) waveSurferRef.current!.seekTo(curTime / dur);
          waveSurferRef.current!.setPlaybackRate(playbackRate);
          if (wasPlaying) {
            waveSurferRef.current!.play();
          }
        });

        prevTheme.current = theme.palette.mode;
      }, [theme, audioUrl, playbackRate, ref, createWS]);

      const handleWaveformClick = (e: React.MouseEvent) => {
        if (isUserRecorded && onWaveformClick) {
          e.preventDefault();
          e.stopPropagation();
          onWaveformClick();
        }
      };

      return (
        <div
          ref={containerRef}
          style={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            cursor: isUserRecorded ? "pointer" : "default",
          }}
          onClick={handleWaveformClick}
        />
      );
    },
  ),
);

interface AudioPlayerProps {
  audioUrl: string;
  isBot: boolean;
  text: string;
  currentReaction?: string;
  messageId: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  embedded?: boolean;
  // Lesson / Crash Course tabbed message title chip; default cyan inline title.
  pillTitle?: boolean;
  hideTitle?: boolean;
  transcription?: string;
  transcriptionTimed?: TranscriptionTimedPayload;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  isBot,
  text,
  currentReaction,
  messageId,
  isSaved,
  markedIds,
  isTags,
  handleRemoveTag,
  handleReactionClick,
  handleAddToFavorite,
  embedded = false,
  pillTitle = false,
  hideTitle = false,
  transcription,
  transcriptionTimed,
}) => {
  const { url: resolvedAudioUrl, isLoading: isResolvingAudioUrl } =
    useAuthenticatedFileUrl(audioUrl);
  const effectiveAudioUrl = resolvedAudioUrl ?? "";
  const { user } = useUserStore();
  const { trackInteraction } = useMessageInteractionTracking(messageId);
  const {
    activeAudioPlayerId,
    setActiveAudioPlayer,
    activeAudioPlayerPause,
    setActiveAudioPlayerPause,
    setMessagesAudio,
    messageAudio: messageAudioUrl,
    messageAudioId,
  } = useChatStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const styles = audioPlayerStyles(theme, embedded, pillTitle);
  const playIconSize = isMobile ? 36 : 48;
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const { isDark } = useAppStore();

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playStart, setPlayStart] = useState<number | null>(null);
  const [isMini, setIsMini] = useState(false);
  const [miniAudio, setMiniAudio] = useState<string | null>(null);
  const [leftPos, setLeftPos] = useState("43.6%");

  const [useFallback, setUseFallback] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const fallbackAudioRef = useRef<HTMLAudioElement | null>(null);
  const triedSyntheticPeaksRef = useRef(false);
  const { isSafari } = useBrowserDetection();

  const isUserRecorded =
    !isBot && (text === "Voice Message" || !text || text.trim() === "");

  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const hasTranscript = !!(
    transcription?.trim() || transcriptionTimed?.segments?.length
  );

  useEffect(() => {
    if (window.MediaRecorder) {
      try {
        [
          "audio/mp4;codecs=mp4a.40.2",
          "audio/mp4",
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/ogg;codecs=opus",
          "audio/ogg",
          "audio/mp3",
        ].filter((type) => MediaRecorder.isTypeSupported(type));
      } catch (err) {
        console.error("Error checking MediaRecorder support:", err);
      }
    }
  }, [effectiveAudioUrl]);

  useEffect(() => {
    triedSyntheticPeaksRef.current = false;
  }, [effectiveAudioUrl]);

  useEffect(() => {
    if (!effectiveAudioUrl) return;
    if (isLoaded || useFallback) return;
    timeoutRef.current = window.setTimeout(() => {
      if (!isLoaded) {
        setUseFallback(true);
      }
    }, 15000);
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [effectiveAudioUrl, isLoaded, useFallback]);

  const fmt = (sec: number) => {
    const s = Math.max(0, Math.round(sec));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  const applyPlaybackRate = useCallback(
    (r: number) => {
      setPlaybackRate(r);
      if (useFallback) {
        const media = fallbackAudioRef.current;
        if (media) media.playbackRate = r;
      } else {
        waveSurferRef.current?.setPlaybackRate(r);
      }
    },
    [useFallback],
  );

  const onSpeed = (e: SelectChangeEvent<number>) => {
    applyPlaybackRate(Number(e.target.value));
  };

  const onSeekTranscript = useCallback(
    (sec: number) => {
      if (useFallback) {
        const media = fallbackAudioRef.current;
        if (!media) return;
        const durationSec = media.duration;
        if (!Number.isFinite(durationSec) || durationSec <= 0) return;

        const clamped = Math.max(0, Math.min(sec, durationSec));
        const seekWithPreroll = Math.max(
          0,
          clamped - TRANSCRIPT_SEEK_PREROLL_SEC,
        );
        media.currentTime = seekWithPreroll;
        void media.play().catch(() => undefined);
        return;
      }

      const ws = waveSurferRef.current;
      if (!ws) return;

      const durationSec = ws.getDuration();
      if (!Number.isFinite(durationSec) || durationSec <= 0) return;

      const clamped = Math.max(0, Math.min(sec, durationSec));
      const seekWithPreroll = Math.max(
        0,
        clamped - TRANSCRIPT_SEEK_PREROLL_SEC,
      );
      ws.seekTo(seekWithPreroll / durationSec);

      try {
        if (!ws.isPlaying()) ws.play();
      } catch (err) {
        console.warn("Failed to play from transcript seek:", err);
      }
    },
    [useFallback],
  );

  const onPlayPause = useCallback(() => {
    if (useFallback) {
      const media = fallbackAudioRef.current;
      if (!media) return;
      if (media.paused) {
        void media
          .play()
          .catch((err) => console.warn("Fallback play failed:", err));
      } else {
        media.pause();
      }
      return;
    }

    if (!isLoaded || !waveSurferRef.current) {
      return;
    }

    // For Safari, add additional check

    if (
      !isPlaying &&
      activeAudioPlayerId &&
      activeAudioPlayerId !== messageId &&
      activeAudioPlayerPause
    ) {
      activeAudioPlayerPause();
    }

    if (isSafari) {
      try {
        waveSurferRef.current.playPause();
      } catch (error) {
        console.warn("Safari audio play error:", error);
        setTimeout(() => {
          try {
            waveSurferRef.current?.playPause();
          } catch (e) {
            console.warn("Safari audio retry failed:", e);
          }
        }, 100);
      }
    } else {
      waveSurferRef.current.playPause();
    }

    const now = !isPlaying;

    if (now) {
      setActiveAudioPlayer(messageId);
      setActiveAudioPlayerPause(() => {
        if (waveSurferRef.current?.isPlaying()) {
          waveSurferRef.current.playPause();
        }
      });
      setPlayStart(Date.now());
      setIsMini(true);
      setMiniAudio(effectiveAudioUrl);
      setMessagesAudio(null, null);
    } else if (playStart) {
      const listened = (Date.now() - playStart) / 1000;
      sendAudioListeningTime(user.id, messageId, listened);
      setPlayStart(null);
    }
  }, [
    isLoaded,
    isPlaying,
    activeAudioPlayerId,
    activeAudioPlayerPause,
    messageId,
    playStart,
    setActiveAudioPlayer,
    setActiveAudioPlayerPause,
    setMessagesAudio,
    effectiveAudioUrl,
    user.id,
    isSafari,
    useFallback,
  ]);

  const toggleMini = () => {
    setIsMini(!isMini);
    if (!isMini) {
      setMiniAudio(effectiveAudioUrl);
    } else {
      setMiniAudio(null);
      if (isPlaying && waveSurferRef.current) {
        waveSurferRef.current.playPause();
      }
    }
  };

  const handleWaveformClick = () => {
    if (isUserRecorded && isPlaying) {
      waveSurferRef.current?.playPause();
    }
  };

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setLeftPos(w < 576 ? "24%" : w < 769 ? "38.5%" : "43.6%");
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [messageAudioUrl, messageAudioId, miniAudio, activeAudioPlayerId]);

  useEffect(() => {
    const ws = waveSurferRef.current;
    if (!ws) {
      return;
    }

    const mediaEl = ws.getMediaElement?.() as HTMLAudioElement | undefined;
    let cancelled = false;

    (async () => {
      try {
        const d = await getAccurateDuration(effectiveAudioUrl);
        if (!cancelled) {
          setDuration(Math.round(d));
        }
      } catch (e) {
        console.warn("Failed to get accurate duration, using fallback:", e);
        const md = mediaEl?.duration;
        if (!cancelled && Number.isFinite(md) && md! > 0) {
          setDuration(Math.round(md!));
        }
      } finally {
        if (!cancelled) {
          setIsLoaded(true);
          ws.setPlaybackRate(playbackRate);
        }
      }
    })();

    const onTimeUpdate = (t?: number) => {
      if (typeof t === "number") {
        setCurrentTime(t);
      } else if (mediaEl) {
        setCurrentTime(mediaEl.currentTime || 0);
      }
    };

    const onTimeUpdateFromMedia = () => {
      // Only use mediaEl.currentTime as fallback when WaveSurfer doesn't provide time
      // In Safari, mediaEl.currentTime can be inaccurate with playback rate changes
      // if (mediaEl) {
      //   setCurrentTime(mediaEl.currentTime || 0);
      // }
    };

    const onMeta = () => {
      const md = mediaEl?.duration;
      if (Number.isFinite(md) && md! > 0) {
        setDuration((prev) => (prev ? prev : Math.round(md!)));
      }
    };
    const finish = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (isMini) {
        setIsMini(false);
        setMiniAudio(null);
      }
      if (playStart) {
        const listened = (Date.now() - playStart) / 1000;
        sendAudioListeningTime(user.id, messageId, listened);
        trackInteraction("audio_play", listened);
        setPlayStart(null);
      }
      if (activeAudioPlayerId === messageId) {
        setActiveAudioPlayer(null);
        setActiveAudioPlayerPause(null);
      }
    };

    const error = (err: unknown) => {
      console.error("WaveSurfer error:", err);
      if (isLoaded || useFallback) return;

      // CORS / fetch failures: keep custom UI with synthetic peaks + media playback.
      if (
        !triedSyntheticPeaksRef.current &&
        waveSurferRef.current &&
        effectiveAudioUrl
      ) {
        triedSyntheticPeaksRef.current = true;
        waveSurferRef.current
          .load(effectiveAudioUrl, [createSyntheticPeaks()])
          .catch((peaksErr: unknown) => {
            if ((peaksErr as DOMException)?.name !== "AbortError") {
              console.error("Synthetic peaks load failed:", peaksErr);
              setUseFallback(true);
            }
          });
        return;
      }

      setUseFallback(true);
    };

    const play = () => setIsPlaying(true);
    const pause = () => setIsPlaying(false);

    ws.on("ready", onMeta);
    ws.on("timeupdate", onTimeUpdate);
    ws.on("seeking", onTimeUpdate);
    ws.on("finish", finish);
    ws.on("error", error);
    ws.on("play", play);
    ws.on("pause", pause);

    if (mediaEl) {
      mediaEl.preload = "metadata";
      mediaEl.addEventListener("loadedmetadata", onMeta);
      mediaEl.addEventListener("durationchange", onMeta);
      mediaEl.addEventListener("timeupdate", onTimeUpdateFromMedia);
    }

    return () => {
      cancelled = true;
      ws.un("ready", onMeta);
      ws.un("timeupdate", onTimeUpdate);
      ws.un("seeking", onTimeUpdate);
      ws.un("finish", finish);
      ws.un("error", error);
      ws.un("play", play);
      ws.un("pause", pause);
      mediaEl?.removeEventListener("loadedmetadata", onMeta);
      mediaEl?.removeEventListener("durationchange", onMeta);
      mediaEl?.removeEventListener("timeupdate", onTimeUpdateFromMedia);
    };
  }, [
    effectiveAudioUrl,
    playbackRate,
    isMini,
    playStart,
    activeAudioPlayerId,
    messageId,
    setActiveAudioPlayer,
    setActiveAudioPlayerPause,
    user.id,
    isLoaded,
    useFallback,
    trackInteraction,
  ]);

  useEffect(() => {
    const h = (e: PromiseRejectionEvent) => {
      if ((e.reason as DOMException)?.name === "AbortError") {
        e.preventDefault();
      } else {
        console.error("Unhandled promise rejection:", e.reason);
      }
    };
    window.addEventListener("unhandledrejection", h);
    return () => window.removeEventListener("unhandledrejection", h);
  }, []);

  if (isResolvingAudioUrl || !effectiveAudioUrl) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (useFallback) {
    return (
      <Box
        sx={{
          ...styles.container,
          maxWidth: embedded ? "100%" : "400px",
          padding: "10px",
          ...(embedded && isMobile ? { overflow: "visible" } : {}),
        }}
      >
        {hasTranscript && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setTranscriptOpen(true)}
              sx={styles.transcriptButton}
              title="View transcript"
            >
              {isMobile ? "Transcript" : "View transcript"}
            </Button>
          </Box>
        )}
        <audio
          ref={fallbackAudioRef}
          controls
          preload="metadata"
          src={effectiveAudioUrl}
          data-message-id={messageId}
          style={{ width: "250px" }}
          onCanPlay={() => {
            setIsLoaded(true);
          }}
          onLoadedMetadata={() => {
            const media = fallbackAudioRef.current;
            setDuration(Number.isFinite(media?.duration) ? media!.duration : 0);
          }}
          onDurationChange={() => {
            const media = fallbackAudioRef.current;
            setDuration(Number.isFinite(media?.duration) ? media!.duration : 0);
          }}
          onTimeUpdate={() => {
            const media = fallbackAudioRef.current;
            setCurrentTime(media?.currentTime || 0);
          }}
          onError={(e) => {
            const audio = e.target as HTMLAudioElement;
            console.error("Native audio player error:", audio.error);
          }}
          onPlay={() => {
            setIsPlaying(true);
            setActiveAudioPlayer(messageId);
            setPlayStart(Date.now());
            trackInteraction("audio_start", 0);
          }}
          onPause={() => {
            setIsPlaying(false);
            if (playStart) {
              const listened = (Date.now() - playStart) / 1000;
              sendAudioListeningTime(user.id, messageId, listened);
              trackInteraction("audio_pause", listened);
              setPlayStart(null);
            }
          }}
        />
        <AudioTranscriptionDialog
          open={transcriptOpen}
          onClose={() => setTranscriptOpen(false)}
          markdown={transcription}
          timed={transcriptionTimed}
          currentTimeSec={currentTime}
          onSeekToSecond={onSeekTranscript}
          audioOverview={{
            audioUrl: effectiveAudioUrl,
            isPlaying,
            currentTimeSec: currentTime,
            durationSec: duration,
            title: text || "Voice Message",
            onPlayPause,
            playbackRate,
            onPlaybackRateChange: applyPlaybackRate,
          }}
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          ...styles.container,
          ...(embedded && isMobile ? { overflow: "visible" } : {}),
        }}
        position="relative"
      >
        {!isLoaded && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: embedded
                ? "rgba(255,255,255,0.15)"
                : "rgba(255,255,255,0.8)",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Loading audio...
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            ...styles.header,
            ...(hasTranscript
              ? {
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 1.25,
                }
              : undefined),
          }}
        >
          {!hideTitle && (
            <Typography
              variant="body2"
              sx={{
                ...styles.text,
                maxWidth: "100%",
                ...(hasTranscript
                  ? {
                      alignSelf: "stretch",
                      width: "100%",
                      flex: "none",
                      minWidth: 0,
                      pr: 0,
                      whiteSpace: "normal",
                      wordBreak: "normal",
                      overflowWrap: "break-word",
                      lineHeight: 1.35,
                    }
                  : isMobile
                    ? {
                        flex: "1 1 0%",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        pr: 1,
                      }
                    : {
                        flex: "1 1 0%",
                        minWidth: 0,
                        pr: 1,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }),
              }}
            >
              {text || "Voice Message"}
            </Typography>
          )}

          <Box
            sx={{
              ...styles.headerActions,
              ...(hasTranscript
                ? {
                    width: "100%",
                    justifyContent: "space-between",
                    gap: 0,
                    columnGap: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                    marginLeft: 0,
                    marginRight: 0,
                  }
                : {}),
            }}
          >
            {hasTranscript && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setTranscriptOpen(true)}
                sx={{
                  ...styles.transcriptButton,
                  marginLeft: 0,
                  marginRight: 0,
                }}
                title="View transcript"
              >
                {isMobile ? "Transcript" : "View transcript"}
              </Button>
            )}
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 64, sm: 60 },
                flexShrink: 0,
                marginLeft: 0,
                marginRight: 0,
              }}
            >
              <Select
                value={playbackRate}
                onChange={onSpeed}
                disabled={!isLoaded}
                sx={styles.speedSelect}
              >
                {[0.5, 1, 1.5, 2].map((v) => (
                  <MenuItem key={v} value={v} sx={{ fontSize: 14 }}>
                    {v}x
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={styles.audioControlsContainer}>
          <Box sx={styles.playButton} onClick={onPlayPause} flexShrink={0}>
            {isLoaded &&
              (!isPlaying ? (
                isDark ? (
                  <PlayIconActiveDark
                    style={{ width: playIconSize, height: playIconSize }}
                  />
                ) : (
                  <PlayActiveLightIcon
                    style={{ width: playIconSize, height: playIconSize }}
                  />
                )
              ) : (
                <PauseActiveLightIcon
                  style={{ width: playIconSize, height: playIconSize }}
                />
              ))}
          </Box>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isLoaded ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            style={{ flex: 1, minWidth: 0, display: "flex" }}
          >
            <Waveform
              ref={waveSurferRef}
              audioUrl={effectiveAudioUrl}
              theme={theme}
              playbackRate={playbackRate}
              isUserRecorded={isUserRecorded}
              onWaveformClick={handleWaveformClick}
              messageId={messageId}
              height={isMobile ? 44 : 64}
            />
          </motion.div>
        </Box>

        <Box sx={styles.timeDisplay}>
          <Typography variant="caption" sx={styles.timeTextLeft}>
            {fmt(Math.min(currentTime, duration || Infinity))}
          </Typography>
          <Typography variant="caption" sx={styles.timeTextRight}>
            {isLoaded ? fmt(duration) : "..."}
          </Typography>
        </Box>

        {!embedded && (
          <ReactionAndFavorite
            currentReaction={currentReaction}
            markedIds={markedIds}
            handleRemoveTag={handleRemoveTag}
            isSaved={isSaved}
            isTags={isTags}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            reactionBg={theme.palette.audioPlayer.bg}
          />
        )}
      </Box>

      <AudioTranscriptionDialog
        open={transcriptOpen}
        onClose={() => setTranscriptOpen(false)}
        markdown={transcription}
        timed={transcriptionTimed}
        currentTimeSec={currentTime}
        onSeekToSecond={onSeekTranscript}
        audioOverview={{
          audioUrl: effectiveAudioUrl,
          isPlaying,
          currentTimeSec: currentTime,
          durationSec: duration,
          title: text || "Voice Message",
          onPlayPause,
          playbackRate,
          onPlaybackRateChange: applyPlaybackRate,
        }}
      />

      {!messageAudioUrl &&
        !messageAudioId &&
        activeAudioPlayerId === messageId && (
          <motion.div
            key={messageId}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: isMini ? 0 : 100, opacity: isMini ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              bottom: 110,
              left: leftPos,
              zIndex: 1000,
            }}
          >
            {miniAudio && (
              <MinimalAudioPlayer
                handleClose={toggleMini}
                audio={miniAudio}
                isInAudioPlayer
                isPlaying={isPlaying}
                playbackRate={playbackRate}
                onPlayPause={onPlayPause}
                onSpeedChange={onSpeed}
              />
            )}
          </motion.div>
        )}
    </>
  );
};

export default AudioPlayer;
